import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/auth.store';

type Role = 'owner' | 'admin' | 'manager' | 'member' | 'viewer';

type NavItem = { to: string; label: string; icon: string; end?: boolean; tour?: string; minRole?: Role };

// Role hierarchy: viewer < member < manager < admin < owner
const ROLE_LEVEL: Record<Role, number> = { viewer: 1, member: 2, manager: 3, admin: 4, owner: 5 };

function canSee(item: { minRole?: Role }, userRole: Role): boolean {
  if (!item.minRole) return true;
  return ROLE_LEVEL[userRole] >= ROLE_LEVEL[item.minRole];
}

function buildNav(t: (k: string) => string): NavItem[] {
  return [
    { to: '/dashboard',     label: t('nav.dashboard'),     icon: '📊', end: true },
    { to: '/envelopes/new', label: t('nav.new_envelope'),  icon: '➕', tour: 'new-envelope', minRole: 'member' },
    { to: '/templates',     label: t('nav.templates'),     icon: '📋', tour: 'templates', minRole: 'member' },
    { to: '/library',       label: t('nav.library'),       icon: '📦' },
    { to: '/admin/users',   label: t('admin.admin_link'),  icon: '👥', minRole: 'admin' },
    { to: '/settings/audit', label: t('nav.history'),      icon: '🕓', minRole: 'admin' },
    { to: '/settings/profile',       label: t('nav.profile'),       icon: '👤' },
    { to: '/settings/signature',     label: t('nav.signature'),     icon: '✍️' },
    { to: '/settings/security',      label: t('nav.security'),      icon: '🔒' },
    { to: '/settings/notifications', label: t('nav.notifications'), icon: '🔔' },
  ];
}

export function Sidebar() {
  const { t } = useTranslation();
  const userRole = (useAuthStore((s) => s.user?.role) ?? 'viewer') as Role;
  const companyName = useAuthStore((s) => s.user?.organization.name) ?? '';
  const [open, setOpen] = useState(() => (typeof window !== 'undefined' ? window.innerWidth >= 1024 : true));

  const nav = buildNav(t).filter((item) => canSee(item, userRole));

  return (
    <aside
      className={cn(
        'bg-white shadow-2xl border-r border-blue-100 transition-all duration-300 ease-in-out flex flex-col flex-shrink-0',
        open ? 'w-72' : 'w-20',
      )}
    >
      {/* Burger header — toggles collapse, shows company name when open */}
      <div
        className="h-16 flex items-center justify-between px-5 border-b border-blue-50 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setOpen((o) => !o)}
      >
        {open && (
          <span className="text-[11px] font-bold text-blue-800 tracking-[0.2em] uppercase truncate">
            {companyName}
          </span>
        )}
        <div className={cn('flex items-center justify-center', !open && 'w-full')}>
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </div>
      </div>

      {/* Menu list */}
      <nav className="flex-1 overflow-y-auto p-2">
        <ul className="space-y-1">
          {nav.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                data-tour={item.tour}
                title={!open ? item.label : undefined}
                className={({ isActive }) =>
                  cn(
                    'w-full flex items-center p-3 rounded-xl transition-all duration-200 no-underline',
                    isActive
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-700',
                  )
                }
              >
                <span className="text-xl min-w-[24px] flex justify-center shrink-0">{item.icon}</span>
                <span
                  className={cn(
                    'ml-4 text-sm font-semibold transition-all duration-300 whitespace-nowrap',
                    open ? 'opacity-100 visible w-auto' : 'opacity-0 invisible w-0 hidden',
                  )}
                >
                  {item.label}
                </span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
}
